/**
 * Transport WebSocket (docs/03 §2, §2.6) — remplace PeerJS. Se connecte à `<base>/rooms/:code`
 * du backend `apps/server`. Même contrat `Transport` que BroadcastChannel : `room.ts` ignore
 * quel transport le porte.
 *
 * La RECONNEXION est encapsulée ici (pas dans `room.ts`) : une chute transitoire après une
 * session ouverte relance la connexion en backoff exponentiel (1 s → 30 s) sans prévenir la
 * salle ; à la ré-ouverture, `room.ts` renvoie un `ping need` et ré-adopte l'état (docs/03 §2.6).
 * Le constructeur `WebSocket` est injectable → tests déterministes (faux socket) et Node (`ws`).
 */
import type { NetHandler, Transport } from './transport';
import { wsBase } from './config';

/** Codes de fermeture applicatifs du serveur (server.ts `CLOSE`) → type d'erreur client. */
export const PERMANENT_CLOSE: Readonly<Record<number, string>> = {
  4004: 'room_not_found',
  4009: 'room_full',
  4008: 'bad_request',
  4029: 'rate_limited',
};

/** Sous-ensemble de `WebSocket` réellement utilisé (compatible navigateur ET paquet `ws`). */
export interface MinimalSocket {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}
export type SocketCtor = new (url: string) => MinimalSocket;

export interface WsTransportOpts {
  /** Base ws (défaut `wsBase()`). */
  url?: string;
  /** Constructeur `WebSocket` injectable (tests / Node). */
  WebSocketImpl?: SocketCtor;
  /** Barème de backoff en ms (défaut 1,2,4,8,16,30 s). */
  backoffMs?: number[];
  /** Nb max de reconnexions consécutives échouées avant d'abandonner (défaut 10). */
  maxReconnects?: number;
}

const DEFAULT_BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];
const OPEN_STATE = 1; // WebSocket.OPEN

export function makeWsTransport(
  code: string,
  _myId: string,
  onEvent: NetHandler,
  opts: WsTransportOpts = {},
): Transport {
  const base = (opts.url ?? wsBase()).replace(/\/+$/, '');
  const Impl = opts.WebSocketImpl ?? (globalThis.WebSocket as unknown as SocketCtor | undefined);
  const backoff = opts.backoffMs ?? DEFAULT_BACKOFF;
  const maxReconnects = opts.maxReconnects ?? 10;
  const url = `${base}/rooms/${code}`;

  let sock: MinimalSocket | null = null;
  let closedByUs = false;
  let everOpened = false;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const clearRetry = (): void => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  function fail(type: string): void {
    onEvent('error', { type });
    onEvent('close');
  }

  function scheduleReconnect(): void {
    clearRetry();
    if (attempt >= maxReconnects) {
      // Serveur durablement injoignable → on rend la main (« Connexion perdue »).
      onEvent('close');
      return;
    }
    const delay = backoff[Math.min(attempt, backoff.length - 1)];
    attempt++;
    retryTimer = setTimeout(connect, delay);
  }

  function connect(): void {
    if (closedByUs) return;
    if (!base || !Impl) {
      fail('network'); // pas d'URL configurée (prod sans VITE_WS_URL) ou pas de WebSocket
      return;
    }
    let socket: MinimalSocket;
    try {
      socket = new Impl(url);
    } catch {
      if (everOpened) scheduleReconnect();
      else fail('network');
      return;
    }
    sock = socket;

    socket.onopen = () => {
      everOpened = true;
      attempt = 0;
      onEvent('open');
    };
    socket.onmessage = (ev) => {
      let data: unknown;
      try {
        data = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data));
      } catch {
        return; // frame non-JSON : ignorée
      }
      onEvent('msg', data);
    };
    socket.onerror = () => {
      /* la fermeture suit toujours l'erreur : tout est géré dans onclose */
    };
    socket.onclose = (ev) => {
      sock = null;
      if (closedByUs) return;
      const permanent = PERMANENT_CLOSE[ev.code];
      if (permanent) {
        fail(permanent); // salle introuvable/pleine, etc. : pas de reconnexion
        return;
      }
      if (!everOpened) {
        fail('network'); // la toute première connexion a échoué
        return;
      }
      scheduleReconnect(); // chute transitoire → reconnexion silencieuse
    };
  }

  connect();

  return {
    send: (m: unknown) => {
      if (sock && sock.readyState === OPEN_STATE) {
        try {
          sock.send(JSON.stringify(m));
        } catch {
          /* socket parti entre-temps : le heartbeat/reconnexion renverra l'état */
        }
      }
    },
    close: () => {
      closedByUs = true;
      clearRetry();
      try {
        sock?.close(1000, 'bye');
      } catch {
        /* déjà fermé */
      }
      sock = null;
    },
  };
}
